import type * as api from '@gitbook/api-client';
import { createPalette } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { toast } from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { apiClient, getSiteSectionGroupChildren } from '../../../state';
import { SiteSectionGroupIcon } from '../SiteSectionGroupIcon';

export interface SiteSectionGroupMoveOptions {
    siteSectionGroup: api.SiteSectionGroup;
    siteStructure: api.SiteStructure;
    nestedSectionGroupsEnabled: boolean;
}

/** Return the destinations allowed when moving a section group. `null` represents the site root. */
export function getSiteSectionGroupMoveDestinations(
    options: SiteSectionGroupMoveOptions
): Array<api.SiteSectionGroup | null> {
    const { siteSectionGroup, siteStructure, nestedSectionGroupsEnabled } = options;
    if (siteStructure.type !== 'sections') {
        return [];
    }

    const destinations: Array<api.SiteSectionGroup | null> = [];
    if (siteSectionGroup.sectionGroup != null) {
        destinations.push(null);
    }

    if (!nestedSectionGroupsEnabled || hasNestedSectionGroups(siteSectionGroup)) {
        return destinations;
    }

    const descendantIds = new Set(getDescendantSectionGroupIds(siteSectionGroup));
    const topLevelGroups = siteStructure.structure.filter(
        (item): item is api.SiteSectionGroup =>
            item.object === 'site-section-group' && item.sectionGroup == null
    );
    destinations.push(
        ...topLevelGroups.filter(
            (group) =>
                group.id !== siteSectionGroup.id &&
                group.id !== siteSectionGroup.sectionGroup &&
                !descendantIds.has(group.id)
        )
    );

    return destinations;
}

export function useMoveSiteSectionGroup(props: {
    organization: api.Organization;
    site: api.Site;
    siteStructure: api.SiteStructure;
    nestedSectionGroupsEnabled: boolean;
}) {
    const { organization, site, siteStructure, nestedSectionGroupsEnabled } = props;

    return useEventCallback((siteSectionGroup: api.SiteSectionGroup) => {
        const destinations = getSiteSectionGroupMoveDestinations({
            siteSectionGroup,
            siteStructure,
            nestedSectionGroupsEnabled,
        });

        return createPalette({
            search: { placeholder: 'Find a group…' },
            groups: [
                {
                    key: 'destinations',
                    commands: destinations.map((destination) => ({
                        key: destination?.id ?? 'site.root',
                        title: destination?.title ?? 'Root of site',
                        icon: destination ? (
                            <SiteSectionGroupIcon siteSectionGroup={destination} />
                        ) : (
                            Icon.DocsSite
                        ),
                        trigger: () =>
                            moveSiteSectionGroup({
                                organization,
                                site,
                                siteSectionGroup,
                                parent: destination?.id ?? null,
                            }),
                    })),
                },
            ],
        });
    });
}

export async function moveSiteSectionGroup(props: {
    organization: api.Organization;
    site: api.Site;
    siteSectionGroup: api.SiteSectionGroup;
    parent: string | null;
}) {
    const { organization, site, siteSectionGroup, parent } = props;
    await toast.catch(async () => {
        await apiClient.siteSectionGroups.updateSiteSectionGroupById(
            organization.id,
            site.id,
            siteSectionGroup.id,
            { parentSectionGroupId: parent },
            { returnPreference: 'minimal' }
        );
        toast.success('Section group moved');
    }, 'Error while moving section group');
}

function hasNestedSectionGroups(siteSectionGroup: api.SiteSectionGroup): boolean {
    return getSiteSectionGroupChildren(siteSectionGroup).some(
        (child) => child.object === 'site-section-group'
    );
}

function getDescendantSectionGroupIds(siteSectionGroup: api.SiteSectionGroup): string[] {
    return getSiteSectionGroupChildren(siteSectionGroup).flatMap((child) =>
        child.object === 'site-section-group'
            ? [child.id, ...getDescendantSectionGroupIds(child)]
            : []
    );
}
